import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom'
import { Box, AppBar, Toolbar, Typography, Container, Button } from '@mui/material'
import { useState, useEffect } from 'react'
import { ThemeProvider } from '@mui/material/styles'
import WorkflowListView from '@/pages/WorkflowListView'
import WorkflowCreateView from '@/pages/WorkflowCreateView'
import WorkflowUpdateView from '@/pages/WorkflowUpdateView'
import DocumentListView from '@/pages/DocumentListView'
import DocumentView from '@/pages/DocumentView'
import DocumentViewDemo from '@/pages/DocumentViewDemo'
import ThemeToggle from '@/components/ThemeToggle'
import { createTheme as createAuroraTheme } from '@/theme'
import { AuthProvider, ProtectedRoute, UserInfo } from '@/components/auth'

// Navigation Menu Component
const NavigationMenu = () => {
  const location = useLocation();
  
  const isActive = (path: string) => {
    if (path === '/documents') {
      return location.pathname === '/' || location.pathname.startsWith('/documents');
    }
    return location.pathname.startsWith(path);
  };
  
  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button
        component={Link}
        to="/workflows"
        variant={isActive('/workflows') ? 'contained' : 'text'}
        size="small"
        sx={{
          textTransform: 'none',
          fontWeight: 500,
          minWidth: 'auto',
          px: 2,
          py: 0.5,
          ...(isActive('/workflows') ? {
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': {
              backgroundColor: 'primary.dark'
            }
          } : {
            color: 'text.primary',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          })
        }}
      >
        Workflows
      </Button>
      <Button
        component={Link}
        to="/documents"
        variant={isActive('/documents') ? 'contained' : 'text'}
        size="small"
        sx={{
          textTransform: 'none',
          fontWeight: 500,
          minWidth: 'auto',
          px: 2,
          py: 0.5,
          ...(isActive('/documents') ? {
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': {
              backgroundColor: 'primary.dark'
            }
          } : {
            color: 'text.primary',
            '&:hover': {
              backgroundColor: 'action.hover'
            }
          })
        }}
      >
        Documents
      </Button>
    </Box>
  );
};

function App() {
  const [mode, setMode] = useState<'light' | 'dark'>('light')
  const theme = createAuroraTheme(mode)

  useEffect(() => {
    document.documentElement.setAttribute('data-aurora-color-scheme', mode)
  }, [mode])

  const toggleTheme = () => {
    setMode(prev => prev === 'light' ? 'dark' : 'light')
  }

  return (
    <AuthProvider>
      <ThemeProvider theme={theme}>
        <Router>
          <ProtectedRoute>
            <Box 
              sx={{ 
                minHeight: '100vh',
                backgroundColor: 'background.elevation1',
                color: 'text.primary'
              }}
            >
              <AppBar 
                position="static" 
                elevation={0} 
                sx={{ 
                  backgroundColor: 'background.paper', 
                  borderBottom: 1, 
                  borderColor: 'divider',
                  backdropFilter: 'blur(8px)',
                  background: 'rgba(255, 255, 255, 0.8)',
                  '&[data-aurora-color-scheme="dark"]': {
                    background: 'rgba(18, 18, 18, 0.8)',
                  }
                }}
              >
                <Toolbar sx={{ minHeight: 64, px: { xs: 2, sm: 3 } }}>
                  <Typography 
                    variant="h6" 
                    component="div" 
                    sx={{ 
                      color: 'text.primary',
                      fontWeight: 600,
                      fontSize: '1.25rem',
                      mr: 4
                    }}
                  >
                    Content Canvas
                  </Typography>
                  
                  {/* Navigation Menu */}
                  <NavigationMenu />
                  
                  <Box sx={{ flexGrow: 1 }} />
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ThemeToggle mode={mode} onToggle={toggleTheme} />
                    <UserInfo />
                  </Box>
                </Toolbar>
              </AppBar>
              <Container maxWidth="lg" sx={{ py: 4 }}>
                <Routes>
                  <Route path="/" element={<Navigate to="/documents" replace />} />
                  <Route path="/documents" element={<DocumentListView />} />
                  <Route path="/workflows" element={<WorkflowListView />} />
                  <Route path="/documents/demo" element={<DocumentViewDemo />} />
                  <Route path="/documents/:id" element={<DocumentView />} />
                  <Route path="/workflows/new" element={<WorkflowCreateView />} />
                  <Route path="/workflows/:id" element={<WorkflowUpdateView />} />
                  <Route path="/workflows/:id/edit" element={<WorkflowUpdateView />} />
                </Routes>
              </Container>
            </Box>
          </ProtectedRoute>
        </Router>
      </ThemeProvider>
    </AuthProvider>
  )
}

export default App
